import React, { useState } from "react";
import styled from "styled-components";
import {
    Field,
    Label,
    Input,
    Select,
    StatusMessage
} from "./FormComponents";
import { generateTimeSlots } from "../utils/timeSlots";
import { secondaryButtonBase } from "../styles/buttonStyles";

const FormSection = styled.section`
    margin-top: 4rem;
    width: 100%;

    @media (max-width: 768px) {
        margin-top: 3rem;
    }
`;

const FormHeader = styled.div`
    margin-bottom: 2rem;

    h3 {
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0 0 0.5rem;
        color: ${({ theme }) => theme.text};
    }

    p {
        color: ${({ theme }) => theme.subtleText};
        margin: 0;
        line-height: 1.6;
    }

    a {
        color: ${({ theme }) => theme.primary};
        text-decoration: underline;

        &:hover {
            filter: brightness(1.2);
        }
    }

    @media (max-width: 768px) {
        margin-bottom: 1.5rem;

        h3 {
            font-size: 1.25rem;
        }

        p {
            font-size: 0.95rem;
        }
    }
`;

const Form = styled.form`
    background: ${({ theme }) => theme.cardBackground};
    border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
    border-radius: 1rem;
    padding: 2rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;

    @media (max-width: 768px) {
        padding: 1.5rem;
    }
`;

const OptionsLabel = styled.div`
    font-size: 0.95rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 0.5rem;
`;

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const OptionItem = styled.label`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    padding: 0.75rem;
    border: 1px solid ${({ theme, selected }) =>
        selected ? "#00bfff" : theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
    };
    border-radius: 0.5rem;
    background: ${({ theme, selected }) =>
        selected ? (theme.mode === "dark" ? "rgba(0,191,255,0.1)" : "rgba(0,191,255,0.05)") : "transparent"
    };
    transition: all 0.2s ease;

    &:hover {
        border-color: #00bfff;
    }

    input[type="radio"] {
        margin: 0;
        cursor: pointer;
    }

    span {
        font-size: 0.95rem;
        color: ${({ theme }) => theme.text};
    }
`;

const SubmitButton = styled.button`
    ${secondaryButtonBase}
    padding: 0.875rem 1.5rem;
    border: 2px solid ${({ theme, $isActive }) => $isActive ? theme.primary : 'rgba(0, 191, 255, 0.3)'};
    border-radius: 999px;
    color: ${({ $isActive }) => $isActive ? '#fff' : 'rgba(0, 191, 255, 0.5)'};
    background: ${({ theme, $isActive }) => $isActive ? theme.primary : 'transparent'};
    margin-top: 0.5rem;
    font-weight: 600;
    transition: all 0.2s ease;
    box-shadow: ${({ $isActive }) => $isActive ? '0 2px 8px rgba(0, 191, 255, 0.3)' : 'none'};

    &:hover:not(:disabled) {
        background: ${({ theme, $isActive }) => $isActive ? theme.primary : `${theme.primary}15`};
        border-color: ${({ theme }) => theme.primary};
        color: ${({ $isActive }) => $isActive ? '#fff' : ({ theme }) => theme.primary};
        transform: translateY(-1px);
        filter: ${({ $isActive }) => $isActive ? 'brightness(1.1)' : 'none'};
        box-shadow: ${({ $isActive }) => $isActive ? '0 4px 12px rgba(0, 191, 255, 0.4)' : 'none'};
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

const ContactForm = () => {
    const { days, timeBlocks } = generateTimeSlots();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        reason: "interview",
        day: "",
        time: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus({ type: "", message: "" });

        try {
            const formDataToSend = new FormData(e.target);

            const response = await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formDataToSend).toString()
            });

            if (response.ok) {
                setSubmitStatus({
                    type: "success",
                    message: "Thanks! Your message has been sent. I'll get back to you soon."
                });
                setFormData({
                    name: "",
                    email: "",
                    company: "",
                    reason: "interview",
                    day: "",
                    time: ""
                });
            } else {
                setSubmitStatus({
                    type: "error",
                    message: "Something went wrong. Please try again."
                });
            }
        } catch (error) {
            setSubmitStatus({
                type: "error",
                message: "Failed to send message. Please try emailing me directly at nlovett15@gmail.com"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const reasonOptions = [
        { value: "interview", label: "Schedule an Interview" },
        { value: "portfolio", label: "Portfolio Review" }
    ];

    // Check if all required fields are filled
    const isFormValid = formData.name && formData.email && formData.day && formData.time && !isSubmitting;

    return (
        <FormSection aria-labelledby="form-title">
            <FormHeader>
                <h3 id="form-title">Prefer email? Send a message</h3>
                <p>Propose a couple of times that work for you and I'll confirm by email.</p>
            </FormHeader>

            <Form
                name="contact-full"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
            >
                <input type="hidden" name="form-name" value="contact-full" />
                <input type="hidden" name="bot-field" />

                {submitStatus.message && (
                    <StatusMessage type={submitStatus.type}>
                        {submitStatus.message}
                    </StatusMessage>
                )}

                <Field>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                    />
                </Field>

                <Field>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@company.com"
                        required
                    />
                </Field>

                <Field>
                    <Label htmlFor="company">Company</Label>
                    <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                    />
                </Field>

                <div>
                    <OptionsLabel>I'm interested in *</OptionsLabel>
                    <OptionsList>
                        {reasonOptions.map((option) => (
                            <OptionItem
                                key={option.value}
                                selected={formData.reason === option.value}
                            >
                                <input
                                    type="radio"
                                    name="reason"
                                    value={option.value}
                                    checked={formData.reason === option.value}
                                    onChange={handleChange}
                                    required
                                />
                                <span>{option.label}</span>
                            </OptionItem>
                        ))}
                    </OptionsList>
                </div>

                <Field>
                    <Label htmlFor="day">Preferred Day *</Label>
                    <Select
                        id="day"
                        name="day"
                        value={formData.day}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select a day</option>
                        {days.map((day) => (
                            <option key={day.value} value={day.value}>
                                {day.label}
                            </option>
                        ))}
                    </Select>
                </Field>

                <Field>
                    <Label htmlFor="time">Preferred Time (PST) *</Label>
                    <Select
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select a time</option>
                        {timeBlocks.map((block) => (
                            <option key={block.value} value={block.value}>
                                {block.label}
                            </option>
                        ))}
                    </Select>
                </Field>

                <SubmitButton
                    type="submit"
                    disabled={!isFormValid}
                    $isActive={isFormValid}
                    aria-busy={isSubmitting}
                >
                    {isSubmitting ? "Sending..." : "Send Message"}
                </SubmitButton>
            </Form>
        </FormSection>
    );
};

export default ContactForm;
